import React, { memo } from "react";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import Common from "us.common";
import { creditorGroup } from "us.collection.admin/actions";
import { CopyCGBMDs } from "us.collection.admin/repository";
import { $Alert, $Select, $Switch } from "us.common/components";
import { RootState } from "us.helper/types";
import { INewCreditorGroup } from "../../Types";
import { CopyCGBMDSchema } from "../../Validations";

const { $Button, $Form, $Popconfirm } = Common.Components;

/**
 * @description - Creditor group copy BMD component
 * @link Design Document -
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 22/09/2025
 */

const CopyToCreditorBMD: React.FC<INewCreditorGroup & PropsFromRedux> = memo(
  (props) => {
    const { t } = useTranslation();
    const {
      creditorGroupDetails,
      onClose,
      creditorGroups,
      copyCreditorBMD,
      isCopyingBMD,
    } = props;

    const { data } = creditorGroups ?? {};

    const handleSubmit = (values: any, actions: any) => {
      if (actions.isValidating) return;
      const request = CopyCGBMDs.call(values);
      copyCreditorBMD && copyCreditorBMD(request);
    };

    return (
      <Formik
        initialValues={{
          ...creditorGroupDetails.data,
          targetCreditorGroupId: "",
          copytoCreditors: false,
        }}
        validationSchema={CopyCGBMDSchema}
        validateOnChange
        validateOnBlur
        validateOnMount
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          isValidating,
          resetForm,
          ...rest
        }: any) => (
          <$Form>
            <div>
              <div>
                <span>
                  {t(
                    "US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.SOURCE_CREDITOR_GROUP"
                  )}
                </span>
                <div>
                  <strong>{values?.groupName}</strong>
                </div>
              </div>
              <div className="mt-3">
                <span>
                  {t(
                    "US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.DESTINATION_CREDITOR_GROUP"
                  )}
                </span>
                <$Select
                  placeholder=""
                  className="w-100"
                  showSearch={false}
                  options={data}
                  allOption={false}
                  optionValue="id"
                  optionText="groupName"
                  formitem={{}}
                  name="targetCreditorGroupId"
                  dropdownMatchSelectWidth={false}
                  allowClear={true}
                />
              </div>
              <div className="mt-3">
                <$Alert
                  message={t(
                    "US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.COPY_BMD_TO_CREDITOR_GROUP_ALERT_MESSAGE"
                  )}
                  type="info"
                  showIcon
                  closable
                  className="mb-3"
                />
              </div>
              <div className="mt-3 d-flex align-items-center">
                <$Switch
                  {...rest}
                  name="copytoCreditors"
                  onChange={(value: any) =>
                    rest.setFieldValue("copytoCreditors", value)
                  }
                  defaultChecked={values.copytoCreditors}
                  checked={values.copytoCreditors}
                />
                <span className="ml-2">
                  {t(
                    "US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.COPY_TO_CREDITOR"
                  )}
                </span>
              </div>
              <div className="mt-3">
                {values.copytoCreditors && (
                  <$Alert
                    message={
                      <span className="copy-to-creditor-alert-warning">
                        {t(
                          "US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.COPY_BMD_TO_CREDITOR_ALERT_MESSAGE"
                        )}
                      </span>
                    }
                    type="info"
                    showIcon
                    closable={false}
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: 0,
                    }}
                    className="plain-red-alert"
                  />
                )}
              </div>
            </div>
            <div className="drawer-footer-fixed align-content-center justify-content-end">
              <div>
                <$Button
                  className="mr-2"
                  disabled={!rest.isValid || isCopyingBMD}
                  loading={isCopyingBMD}
                  onClick={(e) => {
                    handleSubmit(e);
                  }}
                  type="primary"
                >
                  {t("US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.COPY")}
                </$Button>
                {rest.dirty && (
                  <$Popconfirm
                    title={t(
                      "US.COLLECTION.ADMIN:MANAGECREDITORGROUPS.ADDNEWCANCELMSG"
                    )}
                    placement="topLeft"
                    onConfirm={() => onClose()}
                    okText={t("US.COLLECTION.COMMON:COMMON.YES")}
                    cancelText={t("US.COLLECTION.COMMON:COMMON.NO")}
                  >
                    <$Button>{t("US.COLLECTION.COMMON:COMMON.CANCEL")}</$Button>
                  </$Popconfirm>
                )}
                {!rest.dirty && (
                  <$Button onClick={() => onClose()}>
                    {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
                  </$Button>
                )}
              </div>
            </div>
          </$Form>
        )}
      </Formik>
    );
  }
);

const mapStateToProps = (state: RootState) => {
  const { creditorGroup } = state;
  const { creditorGroupDetails, creditorGroups, isCopyingBMD } = creditorGroup;

  return { creditorGroupDetails, creditorGroups, isCopyingBMD };
};

const mapDispatchToProps = {
  copyCreditorBMD: creditorGroup.copyBmd.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CopyToCreditorBMD);
